'use client';

import type { VariantProps } from 'class-variance-authority';
import type { CSSProperties, ImgHTMLAttributes, SyntheticEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DEFAULT_IMAGE } from '@/app/components/media/image/constants';
import { PreloadImage } from '@/app/components/media/image/preload-image';
import { Sources } from '@/app/components/media/image/sources';
import type {
  ImageProps,
  ImageRenderOption,
} from '@/app/components/media/image/types';
import {
  getAdjustedImageSrc,
  getImageQueryParams,
  getImgSrcset,
  isSvg,
} from '@/app/components/media/image/utils';
import { useInView } from '@/app/hooks/useInView';
import { cn } from '@/app/utils/cn';
import { cnva } from '@/app/utils/cnva';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { safeCall } from '@/app/utils/safeCall';

const ImageStyled = cnva('absolute inset-0 size-full object-cover', {
  variants: {
    layout: {
      fill: 'max-h-full min-h-full min-w-full',
      ratio: '',
    },
    loadedEffect: {
      fade: 'transition-image opacity-0',
      none: '',
    },
    hoverEffect: {
      zoom: 'transition-image cursor-pointer hover:scale-105',
    },
    objectFit: {
      contain: 'object-contain',
      cover: 'object-cover',
    },
    objectPosition: {
      top: 'object-top',
      left: 'object-left',
      right: 'object-right',
      bottom: 'object-bottom',
    },
    isLoaded: {
      true: '',
    },
    isGroupHover: {
      true: '',
    },
  },
  compoundVariants: [
    {
      loadedEffect: 'fade',
      isLoaded: true,
      className: 'opacity-100',
    },
    {
      isGroupHover: true,
      hoverEffect: 'zoom',
      className: 'group-hover:scale-105',
    },
  ],
  defaultVariants: {
    loadedEffect: 'fade',
    layout: 'ratio',
  },
});

const PictureStyled = cnva('relative block overflow-hidden', {
  variants: {
    layout: {
      fill: 'absolute inset-0',
      ratio: 'w-full',
    },
    placeholder: {
      shimmer: 'loading-shimmer loading-shimmer-light',
      'dark-shimmer': 'loading-shimmer loading-shimmer-dark',
    },
    showPlaceholder: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      placeholder: ['shimmer', 'dark-shimmer'],
      showPlaceholder: true,
      className: 'loading-shimmer-loading',
    },
  ],
  defaultVariants: {
    layout: 'ratio',
    showPlaceholder: false,
  },
});

export type ImageStyledType = VariantProps<typeof ImageStyled>;
export type PictureStyledType = VariantProps<typeof PictureStyled>;
export const Image = ({
  src,
  alt,
  width,
  height,
  sources = [],
  placeholder,
  loading = 'lazy',
  loadedEffect = 'fade',
  priority = false,
  layout = 'ratio',
  hoverEffect,
  rootRef = null,
  rootMargin = '0px 0px 0px 0px',
  srcSetOptions = {},
  sizes = '100vw',
  className = '',
  imageClassName = '',
  aspectRatio,
  objectFit,
  objectPosition,
  onLoad,
  isGroupHover,
  attributes = {},
}: ImageProps) => {
  // Adjust loading behavior if the image is marked as priority
  if (priority) {
    loading = 'eager';
    loadedEffect = null;
    placeholder = undefined;
  }

  const pictureRef = useRef<HTMLPictureElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const [isLoaded, setIsLoaded] = useState<boolean>(priority);
  const [showPlaceholder, setShowPlaceholder] =
    useState<boolean>(!!placeholder);

  const inView = useInView(pictureRef, {
    root: rootRef,
    rootMargin,
    threshold: 0.01,
  });

  const isSvgSrc = isSvg(src);
  const renderOption: ImageRenderOption =
    sources.length > 0 ? 'sources' : 'sizes';

  const handleImageLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      if (event.currentTarget?.src === DEFAULT_IMAGE) return;
      setIsLoaded(true);
      if (showPlaceholder) {
        setShowPlaceholder(false);
      }
      safeCall(onLoad, event.currentTarget);
    },
    [onLoad, showPlaceholder]
  );

  const memoizedSrc = useMemo(
    () =>
      getAdjustedImageSrc(
        getImageQueryParams(src, srcSetOptions, renderOption),
        DEFAULT_IMAGE,
        inView,
        loading
      ),
    [src, inView, loading, srcSetOptions, renderOption]
  );

  const memoizedSrcSet = useMemo(() => {
    return getImgSrcset(src, srcSetOptions, inView, loading);
  }, [src, srcSetOptions, inView, loading]);

  const imgSrcsetProps = useMemo<ImgHTMLAttributes<HTMLImageElement>>(
    () => ({
      srcSet: isSvgSrc ? undefined : memoizedSrcSet,
      sizes: isSvgSrc ? undefined : sizes,
    }),
    [memoizedSrcSet, isSvgSrc, sizes]
  );

  useEffect(() => {
    /**
     * Note: Fix image onLoad event not fired when image is already loaded in priority
     */
    if (
      !!priority &&
      imgRef.current?.complete &&
      typeof onLoad === 'function'
    ) {
      safeCall(onLoad, imgRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!src) {
    return null;
  }

  return (
    <>
      {!!priority && (
        <PreloadImage
          src={src}
          sources={sources}
          srcSetOptions={srcSetOptions}
          sizes={sizes}
        />
      )}
      <picture
        ref={pictureRef}
        className={PictureStyled({
          layout,
          placeholder,
          showPlaceholder,
          className: cn(
            className,
            aspectRatio !== 'calculate' && aspectRatio, // aspectRatio can be array, so it needs to write this way
            {
              'calc-ratio': aspectRatio === 'calculate',
            }
          ),
        })}
        {...attributes}
        style={
          aspectRatio === 'calculate'
            ? ({
                '--aspect-ratio': `${width} / ${height}`,
                ...(attributes?.style ?? {}),
              } as CSSProperties)
            : attributes?.style
        }
      >
        {renderOption === 'sources' && (
          <Sources loading={loading} inView={inView} sources={sources} />
        )}
        <img
          ref={imgRef}
          {...createDataAttributes({
            test: 'image',
          })}
          src={memoizedSrc}
          alt={alt}
          width={width}
          height={height}
          {...(renderOption === 'sizes' ? imgSrcsetProps : {})}
          className={ImageStyled({
            isLoaded,
            loadedEffect,
            hoverEffect,
            layout,
            objectFit,
            objectPosition,
            isGroupHover,
            className: imageClassName,
          })}
          onLoad={handleImageLoad}
        />
      </picture>
    </>
  );
};
